import React, { useEffect, useState, useRef } from "react";
import { useAuth } from "../../contexts/AuthContext";
import supabase from "../../lib/supabase";
import ProfilePicture from "./ProfilePicture";
import Button from "./Button";
import Icon from "../AppIcon";
import CoverCropModal from "@/components/media/CoverCropModal.jsx";
import { Upload } from "lucide-react";
import AvatarThumb68 from "@/sot/primitives/AvatarThumb68.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";

/**
 * Tokenized, LinkedIn-style profile image uploader.
 * - Patients can use a preset cover (outside this component)
 * - Doctors can upload; supports optional remove & progress UI
 */
export default function ProfilePictureUpload({
  currentImageUrl,
  displayName,
  size = "xl",
  onUploadSuccess,
  onUploadError,
  className = "",
  disabled = false,
  userType = "user", // "user" or "doctor"
  accountLayout = false,
  compactTrigger = false,
  renderPreview,
  onCroppedFile,
  messageNamespace = "",
}) {
  const { user, doctorProfile, refreshProfile } = useAuth();
  const { t } = useLanguage();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [progress, setProgress] = useState(0);
  const [cropSource, setCropSource] = useState("");
  const [showCrop, setShowCrop] = useState(false);
  const [displayImageUrl, setDisplayImageUrl] = useState(currentImageUrl || "");
  const fileInputRef = useRef(null);
  const label = (key, fallback) => messageNamespace ? t(`${messageNamespace}.${key}`, fallback) : fallback;

  useEffect(() => {
    setDisplayImageUrl(currentImageUrl || "");
  }, [currentImageUrl]);

  const MAX = 5 * 1024 * 1024;
  const ALLOWED = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

  const validate = (file) => {
    if (!file) throw new Error("Please select a file.");
    if (!ALLOWED.includes(file.type))
      throw new Error("JPEG/PNG/WebP only, please.");
    if (file.size > MAX) throw new Error("Max file size is 5MB.");
  };

  const fileNameFor = (file) => {
    const ext = file.name.split(".").pop();
    return `${user?.id}_${Date.now()}_${Math.random()
      .toString(36)
      .slice(2)}.${ext}`;
  };

  const uploadToSupabase = async (file) => {
    const bucket = userType === "doctor" ? "doctor-profile-images" : "avatars";
    const path = fileNameFor(file);

    // Supabase currently doesn't provide progress; we simulate for UX
    const timer = setInterval(
      () => setProgress((p) => (p >= 90 ? p : Math.min(90, p + 12))),
      180
    );

    const { data, error } = await supabase.storage.from(bucket).upload(path, file, {
      cacheControl: "3600",
      upsert: true,
    });

    clearInterval(timer);
    if (error) throw error;

    const { data: urlData } = supabase.storage.from(bucket).getPublicUrl(path);
    setProgress(100);
    setTimeout(() => setProgress(0), 600);

    return { path, publicUrl: urlData?.publicUrl };
  };

  const updateProfileRow = async (imageUrl) => {
    if (!user?.id) throw new Error("Not signed in.");
    if (userType === "doctor") {
      const { data, error } = await supabase
        .from("doctor_profiles")
        .update({ profile_image_url: imageUrl })
        .eq("user_id", user.id)
        .select("profile_image_url")
        .single();
      if (error) throw error;
      if (String(data?.profile_image_url || "") !== String(imageUrl || "")) {
        throw new Error("Profile image persistence could not be confirmed.");
      }
      return data;
    } else {
      const { data, error } = await supabase
        .from("profiles")
        .update({ avatar_url: imageUrl })
        .eq("user_id", user.id)
        .select("avatar_url")
        .single();
      if (error) throw error;
      if (String(data?.avatar_url || "") !== String(imageUrl || "")) {
        throw new Error("Profile image persistence could not be confirmed.");
      }
      return data;
    }
  };

  const removeFromStorage = async (imageUrl) => {
    if (!imageUrl) return;
    const bucket = userType === "doctor" ? "doctor-profile-images" : "avatars";
    const parts = imageUrl.split(`/${bucket}/`);
    if (parts.length < 2) return;
    const filePath = parts[1].split("?")[0];
    const { error } = await supabase.storage.from(bucket).remove([filePath]);
    if (error) throw error;
  };

  const uploadSelectedFile = async (file) => {
    if (!file || !user?.id) return;

    setError("");
    setUploading(true);
    setProgress(0);

    try {
      validate(file);
      const { path, publicUrl } = await uploadToSupabase(file);
      if (!publicUrl) throw new Error("Profile image storage did not return a public URL.");
      const persistedProfile = await updateProfileRow(publicUrl);
      const refreshedProfile = await refreshProfile?.();
      const persistedUrl = userType === "doctor"
        ? persistedProfile?.profile_image_url
        : persistedProfile?.avatar_url;
      const refreshedUrl = userType === "doctor"
        ? refreshedProfile?.profile_image_url
        : refreshedProfile?.avatar_url;
      if (refreshedProfile && String(refreshedUrl || "") !== String(persistedUrl || "")) {
        throw new Error("Profile image refresh did not return the saved image.");
      }
      setDisplayImageUrl(persistedUrl);
      onUploadSuccess?.(persistedUrl, { path, publicUrl: persistedUrl });
    } catch (err) {
      setError(err?.message || "Upload failed.");
      onUploadError?.(err);
      setProgress(0);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const onSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !user?.id) return;

    if (!accountLayout) {
      await uploadSelectedFile(file);
      return;
    }

    setError("");
    try {
      validate(file);
      const source = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ""));
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      setCropSource(source);
      setShowCrop(true);
    } catch (err) {
      setError(err?.message || "We couldn’t prepare this image.");
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleCropConfirm = async (blob) => {
    if (!blob) return;
    const type = blob.type || "image/jpeg";
    const ext = type.includes("png") ? "png" : type.includes("webp") ? "webp" : "jpg";
    setShowCrop(false);
    setCropSource("");
    const croppedFile = new File([blob], `profile-image-${Date.now()}.${ext}`, { type });
    if (onCroppedFile) {
      const previewUrl = URL.createObjectURL(blob);
      setDisplayImageUrl(previewUrl);
      onCroppedFile(croppedFile, previewUrl);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    await uploadSelectedFile(croppedFile);
  };

  const onRemove = async () => {
    if (!currentImageUrl || uploading || disabled) return;
    setError("");
    setUploading(true);
    try {
      await updateProfileRow(null);
      await removeFromStorage(currentImageUrl);
      const refreshedProfile = await refreshProfile?.();
      const refreshedUrl = userType === "doctor"
        ? refreshedProfile?.profile_image_url
        : refreshedProfile?.avatar_url;
      if (refreshedProfile && refreshedUrl) {
        throw new Error("Profile image removal did not persist.");
      }
      setDisplayImageUrl("");
      onUploadSuccess?.(null);
    } catch (err) {
      setError(err?.message || "Failed to remove image.");
      onUploadError?.(err);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className={`flex flex-col ${renderPreview ? "items-start" : "items-center"} space-y-4 ${className}`}>
      {/* Picture with hover overlay */}
      <div className="relative group">
        {renderPreview ? renderPreview({
          src: displayImageUrl,
          displayName,
          onSelect: () => !disabled && !uploading && fileInputRef.current?.click(),
          uploading,
        }) : accountLayout ? (
          <AvatarThumb68
            src={displayImageUrl}
            alt={label("alt", "Profile image")}
            shape="circle"
            role="patient"
            fallbackSrc="/icons/circular-avatar-placeholder.svg"
            className="aspect-square"
            style={{ width: 104, height: 104 }}
            imgClassName={displayImageUrl ? "object-cover p-0" : "object-contain p-[6px]"}
          />
        ) : (
          <ProfilePicture
            src={displayImageUrl}
            displayName={displayName}
            size={size}
            loading={uploading}
            verified={userType === "doctor" && doctorProfile?.verified_status === "verified"}
            onClick={() => !disabled && !uploading && fileInputRef.current?.click()}
            badge={null}
          />
        )}

        {!disabled && !accountLayout && (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className={`absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition flex items-center justify-center ${
              uploading ? "opacity-100" : ""
            }`}
            aria-label="Change photo"
          >
            {uploading ? (
              <div className="text-white text-center">
                <Icon name="Loader2" size={16} className="animate-spin mb-1" />
                <div className="text-xs">{Math.round(progress)}%</div>
              </div>
            ) : (
              <Icon name="Camera" size={20} className="text-white" />
            )}
          </button>
        )}

        {/* Thin progress ring */}
        {uploading && progress > 0 && !accountLayout && (
          <svg
            className="absolute inset-0 w-full h-full -rotate-90"
            viewBox="0 0 100 100"
          >
            <circle
              cx="50"
              cy="50"
              r="48"
              fill="none"
              stroke="rgba(255,255,255,0.25)"
              strokeWidth="2"
            />
            <circle
              cx="50"
              cy="50"
              r="48"
              fill="none"
              stroke="rgb(var(--afrimed-primary))"
              strokeWidth="2"
              strokeDasharray={`${progress * 3.01} 301`}
              className="transition-all duration-300"
            />
          </svg>
        )}
      </div>

      {/* Account surfaces use the same full-width image-action contract as the
          Pharmacy account. Existing compact profile editors keep their
          established inline controls. */}
      {!disabled && !compactTrigger && (
        <div className={accountLayout ? "w-full space-y-3" : "flex items-center gap-2"}>
          <Button
            variant={accountLayout ? "tonal" : "outline"}
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
              className={accountLayout ? "w-full min-h-10 gap-2" : "text-xs"}
          >
            {accountLayout ? (
              <Upload className="h-5 w-5" aria-hidden="true" />
            ) : (
              <Icon name="Upload" size={14} className="mr-1" />
            )}
            {uploading ? label("uploading", "Uploading…") : accountLayout ? label("upload", "Upload image") : "Change Photo"}
          </Button>

          {accountLayout ? (
            <Button
              variant="criticalTonal"
              size="sm"
              onClick={onRemove}
              disabled={uploading || !displayImageUrl}
              className="w-full min-h-10 gap-2"
            >
              <img
                src="/icons/doctor/delete_critical.svg"
                alt=""
                aria-hidden="true"
                className="h-5 w-5"
              />
              {label("remove", "Remove image")}
            </Button>
          ) : currentImageUrl ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={onRemove}
              disabled={uploading}
              className="text-xs text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10"
            >
              <Icon name="Trash2" size={14} className="mr-1" />
              Remove
            </Button>
          ) : null}
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-lg">
          <Icon name="AlertCircle" size={16} />
          <span>{error}</span>
        </div>
      )}

      {!disabled && !accountLayout && (
        <p className="text-xs text-muted-foreground text-center max-w-xs">
          Supported: JPEG, PNG, WebP. Max 5MB.
        </p>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={ALLOWED.join(",")}
        onChange={onSelect}
        className="hidden"
        disabled={disabled || uploading}
      />

      {accountLayout && showCrop && cropSource ? (
        <CoverCropModal
          src={cropSource}
          aspect={1 / 1}
          onCancel={() => {
            setShowCrop(false);
            setCropSource("");
            if (fileInputRef.current) fileInputRef.current.value = "";
          }}
          onConfirm={handleCropConfirm}
        />
      ) : null}
    </div>
  );
}
